import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    BadgePercent,
    ChevronDown,
    ChevronRight,
    Clock3,
    FileText,
    Landmark,
    Pencil,
    Plus,
    ReceiptText,
    Search,
    SlidersHorizontal,
    Trash2,
    Briefcase,
    Shield,
    UsersRound
} from "lucide-react";
import {
    payrollDeleteResourceRequest,
    payrollFetchResourceRequest,
    payrollSaveResourceRequest,
} from "../../../redux/actions/payrollActions";
import { getRequest } from "../../../../api";
import "./PaieReferentielPage.css";

const API_BASE_URL = "/api";

const today = () => new Date().toISOString().slice(0, 10);

const PAGE_CONFIG = {
    baremesIr: {
        title: "Barème IR",
        subtitle: "Grille des tranches d'imposition avec taux et somme a déduire, historisee par date d'effet.",
        groupTitle: "Tranches IR",
        groupSubtitle: "Chaque tranche est datee pour permettre le recalcul historique.",
        icon: Landmark,
        addLabel: "Ajouter une tranche IR",
        modalTitle: "tranche IR",
        columns: [
            { key: "trancheMin", label: "Tranche min", type: "money" },
            { key: "trancheMax", label: "Tranche max", type: "money", empty: "Illimitee" },
            { key: "taux", label: "Taux", type: "rate" },
            { key: "sommeADeduire", label: "Somme a déduire", type: "money" },
            { key: "dateDebut", label: "Date de début", type: "date" },
            { key: "dateFin", label: "Date de fin", type: "date", empty: "Actif" },
        ],
        fields: [
            { name: "trancheMin", label: "Tranche min", type: "number", required: true, step: "0.01" },
            { name: "trancheMax", label: "Tranche max", type: "number", step: "0.01", hint: "Vide = derniere tranche illimitee." },
            { name: "taux", label: "Taux", type: "number", required: true, step: "0.0001" },
            { name: "sommeADeduire", label: "Somme a déduire", type: "number", required: true, step: "0.01" },
            { name: "dateDebut", label: "Date de début", type: "date", required: true },
            { name: "dateFin", label: "Date de fin", type: "date" },
        ],
        empty: { trancheMin: "", trancheMax: "", taux: "", sommeADeduire: "", dateDebut: today(), dateFin: "" },
    },
    baremesFraisProfessionnels: {
        title: "Barème des frais professionnels",
        subtitle: "Grille des tranches de revenu annuel avec taux d'abattement et plafond eventuel.",
        groupTitle: "Tranches frais professionnels",
        groupSubtitle: "Le calcul annualise le brut imposable, applique cette grille, puis mensualise.",
        icon: FileText,
        addLabel: "Ajouter une tranche",
        modalTitle: "tranche de frais professionnels",
        columns: [
            { key: "trancheMin", label: "Tranche min", type: "money" },
            { key: "trancheMax", label: "Tranche max", type: "money", empty: "Illimitee" },
            { key: "taux", label: "Taux", type: "rate" },
            { key: "plafondMontant", label: "Plafond", type: "money", empty: "Aucun" },
            { key: "dateDebut", label: "Date de début", type: "date" },
            { key: "dateFin", label: "Date de fin", type: "date", empty: "Actif" },
        ],
        fields: [
            { name: "trancheMin", label: "Tranche min annuelle", type: "number", required: true, step: "0.01" },
            { name: "trancheMax", label: "Tranche max annuelle", type: "number", step: "0.01", hint: "Vide = tranche illimitee." },
            { name: "taux", label: "Taux", type: "number", required: true, step: "0.0001" },
            { name: "plafondMontant", label: "Plafond montant", type: "number", step: "0.01", hint: "Vide = aucun plafond." },
            { name: "dateDebut", label: "Date de début", type: "date", required: true },
            { name: "dateFin", label: "Date de fin", type: "date" },
        ],
        empty: { trancheMin: "", trancheMax: "", taux: "", plafondMontant: "", dateDebut: today(), dateFin: "" },
    },
    typesHeuresSupplementaires: {
        title: "Types d'heures supplementaires",
        subtitle: "Liste des coefficients de majoration par type: 25%, 50%, 100%.",
        groupTitle: "Coefficients heures supplementaires",
        groupSubtitle: "Ces coefficients servent au calcul: heures x taux horaire x (1 + coefficient).",
        icon: Clock3,
        addLabel: "Ajouter un type HS",
        modalTitle: "type d'heure supplementaire",
        columns: [
            { key: "code", label: "Code", type: "code" },
            { key: "libelle", label: "Libelle" },
            { key: "tauxMajoration", label: "Taux de majoration", type: "rate" },
            { key: "coefficient", label: "Coefficient", type: "coefficient" },
            { key: "dateDebut", label: "Date de début", type: "date" },
            { key: "dateFin", label: "Date de fin", type: "date", empty: "Actif" },
        ],
        fields: [
            { name: "code", label: "Code", type: "text", required: true, placeholder: "Ex : HS_25" },
            { name: "libelle", label: "Libelle", type: "text", required: true, placeholder: "Ex : Heures supplementaires 25%" },
            { name: "tauxMajoration", label: "Taux de majoration (décimal)", type: "number", required: true, step: "0.0001", hint: "Ex : 0.25 pour 25%." },
            { name: "coefficient", label: "Coefficient multiplicateur", type: "number", required: true, step: "0.0001", hint: "Ex : 1.25." },
            { name: "dateDebut", label: "Date de début", type: "date", required: true },
            { name: "dateFin", label: "Date de fin", type: "date" },
        ],
        empty: { code: "HS_25", libelle: "", tauxMajoration: "0.25", coefficient: "1.25", dateDebut: today(), dateFin: "" },
    },
    rubriques: {
        title: "Rubriques de paie",
        subtitle: "Referentiel des rubriques: salaire de base, primes, retenues, sous-totaux et lignes calculees.",
        groupTitle: "Référentiel des rubriques",
        groupSubtitle: "Les primes sont distinguées visuellement tout en restant des gains pour le calcul.",
        icon: ReceiptText,
        addLabel: "Ajouter une rubrique",
        modalTitle: "rubrique de paie",
        columns: [
            { key: "code", label: "Code", type: "code" },
            { key: "libelle", label: "Libelle" },
            { key: "type", label: "Type", type: "pill" },
            { key: "categorie", label: "Catégorie", type: "category" },
            { key: "nature", label: "Nature" },
            { key: "soumisCnss", label: "CNSS", type: "boolean" },
            { key: "soumisIr", label: "IR", type: "boolean" },
            { key: "ordreAffichage", label: "Ordre" },
            { key: "active", label: "Statut", type: "status" },
        ],
        fields: [
            { name: "code", label: "Code", type: "text", required: true, placeholder: "Ex : PRIME_ANCIENNETE" },
            { name: "libelle", label: "Libelle", type: "text", required: true, placeholder: "Ex : Prime d'anciennete" },
            { name: "type", label: "Type", type: "select", required: true, options: ["GAIN", "RETENUE", "SOUS_TOTAL"] },
            { name: "categorie", label: "Catégorie", type: "select", required: true, options: ["AUTRE", "PRIME"] },
            { name: "nature", label: "Nature", type: "select", required: true, options: ["FIXE", "VARIABLE", "PERIODIQUE", "CALCULE"] },
            { name: "soumisCnss", label: "Soumis CNSS", type: "checkbox" },
            { name: "soumisIr", label: "Soumis IR", type: "checkbox" },
            { name: "ordreAffichage", label: "Ordre affichage", type: "number", required: true },
            { name: "active", label: "Rubrique active", type: "checkbox" },
            { name: "formule", label: "Note (optionnel)", type: "textarea", full: true },
        ],
        empty: {
            code: "",
            libelle: "",
            type: "GAIN",
            categorie: "AUTRE",
            nature: "VARIABLE",
            soumisCnss: true,
            soumisIr: true,
            ordreAffichage: 100,
            active: true,
            formule: "",
            modeCalcul: "",
            montantFixe: "",
            pourcentage: "",
            moisVersement: "",
            typesContrat: [],
            tranches: [],
            cibles: []
        },
    },
};

const formatMoney = (value, empty = "-") => {
    const number = Number(value);
    if (!Number.isFinite(number)) return empty;
    return number.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatRate = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return "-";
    return `${number.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} %`;
};

const formatDate = (value, empty = "-") => {
    if (!value) return empty;
    return new Date(value).toLocaleDateString("fr-FR");
};

const normalizePayload = (fields, form) => {
    const payload = {};
    fields.forEach((field) => {
        const value = form[field.name];
        if (field.type === "checkbox") {
            payload[field.name] = Boolean(value);
        } else if (field.type === "number") {
            payload[field.name] = value === "" || value === null || value === undefined ? null : Number(value);
        } else {
            payload[field.name] = value === "" ? null : value;
        }
    });
    return payload;
};

const StatusPill = ({ active }) => (
    <span className={`prp-toggle ${active ? "on" : "off"}`}>
        <span className="prp-toggle-knob" />
        <span>{active ? "Actif" : "Inactif"}</span>
    </span>
);

const ReferentielModal = ({ config, open, editing, loading, onClose, onSubmit }) => {
    const [form, setForm] = useState(config.empty);
    const [activeTab, setActiveTab] = useState("identite");

    // References for cascading cibles (only used for rubriques)
    const [emplois, setEmplois] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedEmploiId, setSelectedEmploiId] = useState("");
    const [selectedJobId, setSelectedJobId] = useState("");

    useEffect(() => {
        if (!open) return;
        setActiveTab("identite");
        setSelectedEmploiId("");
        setSelectedJobId("");

        if (editing) {
            const next = { ...config.empty };
            config.fields.forEach((field) => {
                next[field.name] = editing[field.name] ?? config.empty[field.name] ?? "";
            });
            // Extra rubrique calculation fields
            next.modeCalcul = editing.modeCalcul || "";
            next.montantFixe = editing.montantFixe || "";
            next.pourcentage = editing.pourcentage || "";
            next.moisVersement = editing.moisVersement || "";
            next.typesContrat = editing.typesContrat || [];
            next.tranches = editing.tranches || [];
            next.cibles = editing.cibles || [];
            setForm(next);
        } else {
            setForm(config.empty);
        }

        if (config.modalTitle === "rubrique de paie") {
            const token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
            getRequest({ url: `${API_BASE_URL}/emplois`, token }).then(res => setEmplois(res.data || [])).catch(() => {});
            getRequest({ url: `${API_BASE_URL}/jobs`, token }).then(res => setJobs(res.data || [])).catch(() => {});
            getRequest({ url: `${API_BASE_URL}/users`, token }).then(res => setUsers(res.data || [])).catch(() => {});
        }
    }, [config, editing, open]);

    // Cascading filter lists
    const filteredEmplois = useMemo(() => {
        if (!form.typesContrat || form.typesContrat.length === 0) return emplois;
        const matchingUsers = users.filter(u => form.typesContrat.includes(u.typeContrat));
        const matchingJobIds = matchingUsers.map(u => u.job?.id).filter(Boolean);
        const matchingJobs = jobs.filter(j => matchingJobIds.includes(j.id));
        const matchingEmploiIds = matchingJobs.map(j => j.emploi?.id).filter(Boolean);
        return emplois.filter(e => matchingEmploiIds.includes(e.id));
    }, [emplois, users, jobs, form.typesContrat]);

    const filteredJobs = useMemo(() => {
        let list = jobs;
        if (form.typesContrat && form.typesContrat.length > 0) {
            const matchingUsers = users.filter(u => form.typesContrat.includes(u.typeContrat));
            const matchingJobIds = matchingUsers.map(u => u.job?.id).filter(Boolean);
            list = list.filter(j => matchingJobIds.includes(j.id));
        }
        if (selectedEmploiId) list = list.filter(j => j.emploi?.id === Number(selectedEmploiId));
        return list;
    }, [jobs, users, form.typesContrat, selectedEmploiId]);

    const filteredUsers = useMemo(() => {
        let list = users;
        if (form.typesContrat && form.typesContrat.length > 0) list = list.filter(u => form.typesContrat.includes(u.typeContrat));
        if (selectedJobId) {
            list = list.filter(u => u.job?.id === Number(selectedJobId));
        } else if (selectedEmploiId) {
            const matchingJobIds = jobs.filter(j => j.emploi?.id === Number(selectedEmploiId)).map(j => j.id);
            list = list.filter(u => u.job && matchingJobIds.includes(u.job.id));
        }
        return list;
    }, [users, jobs, form.typesContrat, selectedEmploiId, selectedJobId]);

    if (!open) return null;

    const isRubrique = config.modalTitle === "rubrique de paie";
    const showAdvanced = isRubrique && (form.type === "GAIN" || form.type === "RETENUE");

    const change = (event) => {
        const { name, value, type, checked } = event.target;
        setForm((previous) => {
            const next = { ...previous, [name]: type === "checkbox" ? checked : value };
            if (name === "categorie" && value === "PRIME") next.type = "GAIN";
            return next;
        });
    };

    const handleContractTypeChange = (type) => {
        setForm(prev => {
            const current = prev.typesContrat || [];
            const index = current.indexOf(type);
            const next = [...current];
            if (index > -1) next.splice(index, 1); else next.push(type);
            return { ...prev, typesContrat: next };
        });
    };

    const handleToggleTarget = (typeCible, targetId) => {
        setForm(prev => {
            let nextCibles = [...(prev.cibles || [])];
            const index = nextCibles.findIndex(c => {
                if (c.typeCible !== typeCible) return false;
                if (typeCible === "EMPLOI") return c.emploiId === targetId;
                if (typeCible === "POSTE") return c.jobId === targetId;
                if (typeCible === "UTILISATEUR") return c.userId === targetId;
                return false;
            });
            if (index > -1) {
                nextCibles.splice(index, 1);
            } else {
                const newCible = { typeCible };
                if (typeCible === "EMPLOI") { const emp = emplois.find(e => e.id === targetId); newCible.emploiId = targetId; newCible.emploiNom = emp?.name || ""; }
                else if (typeCible === "POSTE") { const jb = jobs.find(j => j.id === targetId); newCible.jobId = targetId; newCible.jobNom = jb?.name || ""; }
                else if (typeCible === "UTILISATEUR") { const u = users.find(us => us.id === targetId); newCible.userId = targetId; newCible.userNom = u ? (u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim()) : ""; }
                nextCibles.push(newCible);
            }
            return { ...prev, cibles: nextCibles };
        });
    };

    const handleAddTranche = () => setForm(prev => ({ ...prev, tranches: [...(prev.tranches || []), { minAnnees: 0, maxAnnees: "", taux: 0 }] }));
    const handleRemoveTranche = (index) => setForm(prev => { const t = [...prev.tranches]; t.splice(index, 1); return { ...prev, tranches: t }; });
    const handleTrancheChange = (index, name, value) => setForm(prev => { const t = [...prev.tranches]; t[index] = { ...t[index], [name]: value === "" ? "" : Number(value) }; return { ...prev, tranches: t }; });

    const submit = (event) => {
        event.preventDefault();
        const missing = config.fields.find((field) => field.required && (form[field.name] === "" || form[field.name] === null || form[field.name] === undefined));
        if (missing) { Swal.fire({ icon: "warning", text: `${missing.label} est obligatoire.` }); return; }

        const payload = normalizePayload(config.fields, form);

        if (isRubrique) {
            payload.modeCalcul = form.modeCalcul || null;
            payload.montantFixe = form.modeCalcul === "FIXE" ? Number(form.montantFixe) : null;
            payload.pourcentage = form.modeCalcul === "POURCENTAGE" ? Number(form.pourcentage) : null;
            payload.moisVersement = form.moisVersement ? Number(form.moisVersement) : null;
            payload.typesContrat = form.typesContrat || [];
            payload.tranches = form.modeCalcul === "TRANCHE_ANCIENNETE"
                ? (form.tranches || []).map(t => ({ minAnnees: Number(t.minAnnees), maxAnnees: t.maxAnnees === "" || t.maxAnnees === null ? null : Number(t.maxAnnees), taux: Number(t.taux) }))
                : [];
            payload.cibles = (form.cibles || []).map(c => ({ typeCible: c.typeCible, emploiId: c.emploiId || null, jobId: c.jobId || null, userId: c.userId || null }));
        }

        onSubmit(payload);
    };

    const renderField = (field) => {
        if (field.type === "checkbox") return (
            <label className="prp-check-label" key={field.name}>
                <input type="checkbox" name={field.name} checked={Boolean(form[field.name])} onChange={change} />{field.label}
            </label>
        );
        if (field.type === "select") return (
            <div className={`prp-field ${field.full ? "prp-full" : ""}`} key={field.name}>
                <label>{field.label}{field.required ? " *" : ""}</label>
                <select name={field.name} value={form[field.name] ?? ""} onChange={change}>
                    {(field.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
            </div>
        );
        if (field.type === "textarea") return (
            <div className={`prp-field ${field.full ? "prp-full" : ""}`} key={field.name}>
                <label>{field.label}</label>
                <textarea name={field.name} value={form[field.name] ?? ""} onChange={change} rows={3} />
            </div>
        );
        return (
            <div className={`prp-field ${field.full ? "prp-full" : ""}`} key={field.name}>
                <label>{field.label}{field.required ? " *" : ""}</label>
                <input name={field.name} type={field.type} step={field.step} value={form[field.name] ?? ""} onChange={change} placeholder={field.placeholder || ""} />
                {field.hint && <span className="prp-field-hint">{field.hint}</span>}
            </div>
        );
    };

    return (
        <div className="prp-overlay" onClick={onClose}>
            <div className="prp-modal" style={showAdvanced ? { maxWidth: "800px" } : {}} onClick={(event) => event.stopPropagation()}>
                <div className="prp-modal-header">
                    <h3>{editing ? "Modifier" : "Ajouter"} {config.modalTitle}</h3>
                    <button className="prp-modal-close" type="button" onClick={onClose}>✕</button>
                </div>

                {showAdvanced && (
                    <div className="prp-modal-tabs">
                        <button type="button" className={`prp-modal-tab ${activeTab === "identite" ? "active" : ""}`} onClick={() => setActiveTab("identite")}>Informations Générales</button>
                        {form.modeCalcul === "TRANCHE_ANCIENNETE" && (
                            <button type="button" className={`prp-modal-tab ${activeTab === "tranches" ? "active" : ""}`} onClick={() => setActiveTab("tranches")}>Grille d'Ancienneté</button>
                        )}
                        <button type="button" className={`prp-modal-tab ${activeTab === "cibles" ? "active" : ""}`} onClick={() => setActiveTab("cibles")}>Population Cible</button>
                    </div>
                )}

                <form className="prp-form" onSubmit={submit}>

                    {/* TAB: Identite */}
                    {(!showAdvanced || activeTab === "identite") && (
                        <>
                            <div className="prp-section-title">Informations</div>
                            <div className="prp-grid">
                                {config.fields.map(renderField)}

                                {showAdvanced && (
                                    <>
                                        <div className="prp-full prp-section-title" style={{ marginTop: 14 }}>Règles de Calcul Automatique</div>
                                        <div className="prp-field">
                                            <label>Mode de calcul</label>
                                            <select name="modeCalcul" value={form.modeCalcul ?? ""} onChange={change}>
                                                <option value="">Manuel / Variable (ElementVariablePaie)</option>
                                                <option value="FIXE">Montant fixe automatique</option>
                                                <option value="POURCENTAGE">Pourcentage du salaire de base</option>
                                                <option value="TRANCHE_ANCIENNETE">Tranches d'ancienneté</option>
                                            </select>
                                        </div>
                                        {form.modeCalcul === "FIXE" && (
                                            <div className="prp-field">
                                                <label>Montant Fixe (DH) *</label>
                                                <input type="number" name="montantFixe" value={form.montantFixe ?? ""} onChange={change} placeholder="0.00" step="0.01" />
                                            </div>
                                        )}
                                        {form.modeCalcul === "POURCENTAGE" && (
                                            <div className="prp-field">
                                                <label>Pourcentage (%) *</label>
                                                <input type="number" name="pourcentage" value={form.pourcentage ?? ""} onChange={change} placeholder="Ex: 5.00" step="0.01" />
                                            </div>
                                        )}
                                        {form.modeCalcul !== "TRANCHE_ANCIENNETE" && (
                                            <div className="prp-field">
                                                <label>Mois de versement (optionnel)</label>
                                                <select name="moisVersement" value={form.moisVersement ?? ""} onChange={change}>
                                                    <option value="">Tous les mois (Mensuel)</option>
                                                    {["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"].map((m, i) => (
                                                        <option key={i + 1} value={i + 1}>{m}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    )}

                    {/* TAB: Tranches */}
                    {showAdvanced && activeTab === "tranches" && form.modeCalcul === "TRANCHE_ANCIENNETE" && (
                        <>
                            <div className="prp-section-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span>Grille d'Ancienneté</span>
                                <button type="button" className="prp-btn prp-btn-assign" style={{ padding: "4px 10px", fontSize: 11 }} onClick={handleAddTranche}>
                                    <Plus size={12} /> Ajouter une tranche
                                </button>
                            </div>
                            <table className="prp-tranches-table">
                                <thead><tr><th>Min (ans)</th><th>Max (ans)</th><th>Taux (%)</th><th></th></tr></thead>
                                <tbody>
                                    {(form.tranches || []).length === 0 ? (
                                        <tr><td colSpan="4" style={{ textAlign: "center", color: "#6b7280", padding: "16px 0" }}>Aucune tranche. Cliquez sur "Ajouter".</td></tr>
                                    ) : form.tranches.map((t, idx) => (
                                        <tr key={idx}>
                                            <td><input type="number" value={t.minAnnees} onChange={(e) => handleTrancheChange(idx, "minAnnees", e.target.value)} style={{ width: "100%", padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 6 }} /></td>
                                            <td><input type="number" value={t.maxAnnees} placeholder="Illimité" onChange={(e) => handleTrancheChange(idx, "maxAnnees", e.target.value)} style={{ width: "100%", padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 6 }} /></td>
                                            <td><input type="number" value={t.taux} step="0.01" onChange={(e) => handleTrancheChange(idx, "taux", e.target.value)} style={{ width: "100%", padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 6 }} /></td>
                                            <td><button type="button" className="prp-link-btn danger" onClick={() => handleRemoveTranche(idx)}><Trash2 size={14} /></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {/* TAB: Cibles */}
                    {showAdvanced && activeTab === "cibles" && (
                        <>
                            <div className="prp-section-title">Type de Contrat (filtrage AND)</div>
                            <div style={{ display: "flex", gap: 20, marginBottom: 16 }}>
                                {["CDI", "CDD"].map(type => (
                                    <label key={type} className="prp-check-label">
                                        <input type="checkbox" checked={(form.typesContrat || []).includes(type)} onChange={() => handleContractTypeChange(type)} />
                                        {type}
                                    </label>
                                ))}
                                <span style={{ fontSize: 11, color: "#6b7280" }}>Si vide → tous les types de contrat</span>
                            </div>

                            <div className="prp-section-title">Structure & Collaborateurs (ciblage OR)</div>
                            <p style={{ fontSize: 11, color: "#6b7280", margin: "-6px 0 10px 0" }}>Si aucune cible sélectionnée → s'applique à <strong>TOUS</strong> les employés.</p>

                            <div className="prp-cascade-container">
                                <div className="prp-cascade-column">
                                    <div className="prp-cascade-header"><span>Emploi</span><Briefcase size={12} /></div>
                                    <div className="prp-cascade-list">
                                        {filteredEmplois.map(e => {
                                            const isChecked = (form.cibles || []).some(c => c.typeCible === "EMPLOI" && c.emploiId === e.id);
                                            return (
                                                <div key={e.id} className={`prp-cascade-item ${selectedEmploiId === String(e.id) ? "active" : ""}`} onClick={() => setSelectedEmploiId(selectedEmploiId === String(e.id) ? "" : String(e.id))}>
                                                    <input type="checkbox" checked={isChecked} onChange={(ev) => { ev.stopPropagation(); handleToggleTarget("EMPLOI", e.id); }} />
                                                    <span>{e.name}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="prp-cascade-column">
                                    <div className="prp-cascade-header"><span>Poste</span><Shield size={12} /></div>
                                    <div className="prp-cascade-list">
                                        {filteredJobs.map(j => {
                                            const isChecked = (form.cibles || []).some(c => c.typeCible === "POSTE" && c.jobId === j.id);
                                            return (
                                                <div key={j.id} className={`prp-cascade-item ${selectedJobId === String(j.id) ? "active" : ""}`} onClick={() => setSelectedJobId(selectedJobId === String(j.id) ? "" : String(j.id))}>
                                                    <input type="checkbox" checked={isChecked} onChange={(ev) => { ev.stopPropagation(); handleToggleTarget("POSTE", j.id); }} />
                                                    <span>{j.name}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="prp-cascade-column">
                                    <div className="prp-cascade-header"><span>Utilisateur</span><UsersRound size={12} /></div>
                                    <div className="prp-cascade-list">
                                        {filteredUsers.map(u => {
                                            const isChecked = (form.cibles || []).some(c => c.typeCible === "UTILISATEUR" && c.userId === u.id);
                                            const name = u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username;
                                            return (
                                                <div key={u.id} className="prp-cascade-item" onClick={() => handleToggleTarget("UTILISATEUR", u.id)}>
                                                    <input type="checkbox" checked={isChecked} onChange={() => {}} />
                                                    <span>{name}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="prp-modal-actions">
                        <button type="button" className="prp-btn prp-btn-outline" onClick={onClose} disabled={loading}>Annuler</button>
                        <button type="submit" className="prp-btn prp-btn-primary" disabled={loading}>{editing ? "Enregistrer" : "Créer"}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const PaieReferentielPage = ({ resource }) => {
    const dispatch = useDispatch();
    const payroll = useSelector((state) => state.payroll || {});
    const config = PAGE_CONFIG[resource];
    const Icon = config.icon || ReceiptText;
    const rows = payroll[resource] || [];
    const [searchTerm, setSearchTerm] = useState("");
    const [rubriqueFilter, setRubriqueFilter] = useState("all");
    const [openGroup, setOpenGroup] = useState(true);
    const [modal, setModal] = useState({ open: false, editing: null });

    useEffect(() => {
        dispatch(payrollFetchResourceRequest(resource));
    }, [dispatch, resource]);

    const filtered = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        return rows.filter((row) => {
            const matchesSearch = !q || JSON.stringify(row).toLowerCase().includes(q);
            const matchesRubrique = resource !== "rubriques" || rubriqueFilter === "all"
                || (rubriqueFilter === "PRIME" ? row.categorie === "PRIME" : row.type === rubriqueFilter);
            return matchesSearch && matchesRubrique;
        });
    }, [resource, rows, searchTerm, rubriqueFilter]);

    const closeModal = () => setModal({ open: false, editing: null });

    const save = (payload) => {
        dispatch(payrollSaveResourceRequest(resource, payload, modal.editing?.id || null));
        closeModal();
    };

    const deleteRow = async (row) => {
        const label = row.code || row.libelle || row.id;
        const result = await Swal.fire({
            title: "Supprimer",
            text: `Supprimer "${label}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#334785",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
        });
        if (result.isConfirmed) {
            dispatch(payrollDeleteResourceRequest(resource, row.id));
        }
    };

    const renderValue = (row, column) => {
        const value = row[column.key];
        if (column.type === "code") return <span className="prp-code">{value}</span>;
        if (column.type === "money") return formatMoney(value, column.empty || "-");
        if (column.type === "rate") return formatRate(value);
        if (column.type === "coefficient") return Number(value).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
        if (column.type === "date") return formatDate(value, column.empty || "-");
        if (column.type === "boolean") return value ? "Oui" : "Non";
        if (column.type === "status") return <StatusPill active={value} />;
        if (column.type === "pill") return <span className={`prp-type prp-type-${String(value || "").toLowerCase()}`}>{value}</span>;
        if (column.type === "category") {
            const isPrime = value === "PRIME";
            return <span className={`prp-category ${isPrime ? "prime" : "other"}`}>{isPrime ? "Prime" : "Autre"}</span>;
        }
        return value || column.empty || "-";
    };

    return (
        <div className="prp-page">
            <div className="prp-header">
                <div>
                    <h2><Icon size={18} />{config.title}</h2>
                    <p className="prp-subtitle">{config.subtitle}</p>
                </div>
                <div className="prp-header-actions">
                    <button className="prp-btn prp-btn-assign" type="button" onClick={() => dispatch(payrollFetchResourceRequest(resource))}>
                        <SlidersHorizontal size={14} />
                        Actualiser
                    </button>
                    <button className="prp-btn prp-btn-primary" type="button" onClick={() => setModal({ open: true, editing: null })}>
                        <Plus size={14} />
                        {config.addLabel}
                    </button>
                </div>
            </div>

            <div className="prp-toolbar">
                <div className="prp-search-wrap">
                    <Search size={15} />
                    <input className="prp-search" placeholder="Rechercher..." value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
                </div>
                {resource === "rubriques" && (
                    <div className="prp-filter-group">
                        <span className="prp-filter-label">Afficher</span>
                        <div className="prp-filter-tabs">
                            {[
                                { key: "all", label: "Toutes" },
                                { key: "PRIME", label: "Primes" },
                                { key: "GAIN", label: "Gains" },
                                { key: "RETENUE", label: "Retenues" },
                                { key: "SOUS_TOTAL", label: "Sous-totaux" },
                            ].map((filter) => (
                                <button key={filter.key} className={`prp-filter-tab ${rubriqueFilter === filter.key ? "active" : ""}`} type="button" onClick={() => setRubriqueFilter(filter.key)}>
                                    {filter.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className="prp-meta">{payroll.loading ? "Chargement..." : `${filtered.length} ligne(s)`}</div>
            </div>

            {payroll.error && <div className="prp-alert prp-alert-danger">{payroll.error}</div>}

            <div className="prp-group">
                <button className="prp-group-header" type="button" onClick={() => setOpenGroup((value) => !value)}>
                    <div className="prp-group-left">
                        <BadgePercent size={16} className="prp-group-icon" />
                        <div>
                            <div className="prp-group-title">{config.groupTitle}</div>
                            <div className="prp-group-subtitle">{config.groupSubtitle}</div>
                        </div>
                    </div>
                    {openGroup ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>

                {openGroup && (
                    <div className="prp-group-body">
                        <div className="prp-table-wrapper">
                            {filtered.length === 0 ? (
                                <div className="prp-empty">Aucune donnée trouvée</div>
                            ) : (
                                <table className="prp-table">
                                    <thead>
                                    <tr>
                                        {config.columns.map((column) => <th key={column.key}>{column.label}</th>)}
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filtered.map((row) => (
                                        <tr key={row.id}>
                                            {config.columns.map((column) => (
                                                <td key={column.key}>{renderValue(row, column)}</td>
                                            ))}
                                            <td className="prp-row-actions">
                                                <button className="prp-link-btn" type="button" title="Modifier" onClick={() => setModal({ open: true, editing: row })}>
                                                    <Pencil size={14} />
                                                </button>
                                                <button className="prp-link-btn danger" type="button" title="Supprimer" onClick={() => deleteRow(row)}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <ReferentielModal config={config} open={modal.open} editing={modal.editing} loading={payroll.saving} onClose={closeModal} onSubmit={save} />
        </div>
    );
};

export const ParamBaremeIr = () => <PaieReferentielPage resource="baremesIr" />;
export const ParamBaremeFraisProfessionnels = () => <PaieReferentielPage resource="baremesFraisProfessionnels" />;
export const ParamTypesHeuresSupplementaires = () => <PaieReferentielPage resource="typesHeuresSupplementaires" />;
export const ParamRubriquesPaie = () => <PaieReferentielPage resource="rubriques" />;

export default PaieReferentielPage;
